import { z } from 'zod';

import { request } from '@/lib/api';
import { authResponseSchema, type TradeKey } from '@/lib/schemas';

/* ---------------------------------------------------------------------------
   ההצטרפות דרך Google (ADR 0002): הרשמה → חיבור Google → סריקה → אישור.
   --------------------------------------------------------------------------- */

export const setupStageSchema = z.enum([
  'SIGNED_UP',
  'GOOGLE_CONNECTED',
  'IMPORTING',
  'REVIEW',
  'DONE',
]);
export type SetupStage = z.infer<typeof setupStageSchema>;

const integrationStatus = z.enum(['CONNECTED', 'EXPIRED', 'ERROR', 'DISCONNECTED']).nullable();

export const setupStatusSchema = z.object({
  stage: setupStageSchema,
  fakeGoogle: z.boolean(),
  google: z.object({
    gmail: integrationStatus,
    drive: integrationStatus,
    // שלב 1 של אימות Google: בלי קריאת מייל — הייבוא מ-Drive בלבד.
    mailReadAvailable: z.boolean().default(true),
    mailRead: z.boolean().default(true),
  }),
  greenInvoice: integrationStatus.default(null),
  run: z
    .object({
      id: z.string(),
      // DOCUMENTS — "למידה ממסמכים" שהועלו באשף (בלי Google).
      source: z.enum(['ONBOARDING', 'DOCUMENTS']).default('ONBOARDING'),
      status: z.enum(['QUEUED', 'RUNNING', 'REVIEW', 'COMPLETED', 'FAILED']),
      phase: z.string().nullable(),
      emailsScanned: z.number(),
      docsParsed: z.number(),
      error: z.string().nullable(),
    })
    .nullable(),
  suggestions: z.array(
    z.object({
      kind: z.enum(['CUSTOMER', 'PRICE_ITEM', 'JOB_TYPE', 'SUPPLIER', 'MATERIAL']),
      status: z.enum(['PENDING', 'ACCEPTED', 'REJECTED']),
      count: z.number(),
    }),
  ),
});
export type SetupStatus = z.infer<typeof setupStatusSchema>;

export const customerPayload = z.object({
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  taxId: z.string().nullish(),
  greenInvoiceId: z.string().nullish(),
  messageCount: z.number(),
  lastContactAt: z.string().nullable(),
  // "למידה ממסמכים": בכמה מסמכים הופיע, ולקוח קיים שההצעה משלימה לו פרטים.
  address: z.string().nullish(),
  documentCount: z.number().default(0),
  existingId: z.string().nullish(),
});
export const pricePayload = z.object({
  code: z.string(),
  description: z.string(),
  price: z.number(),
  minPrice: z.number().nullable(),
  maxPrice: z.number().nullable(),
  category: z.enum(['SERVICE', 'MATERIAL', 'HARDWARE']).optional(),
  unit: z.string().nullish(),
  // פריט שכבר במחירון: ההצעה מעדכנת את המחיר שלו.
  existingId: z.string().nullish(),
  currentPrice: z.number().nullish(),
});
export const supplierPayload = z.object({
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  taxId: z.string().nullable(),
});
export const materialPayload = pricePayload.extend({
  category: z.enum(['MATERIAL', 'HARDWARE']),
  cost: z.number(),
  supplierName: z.string().nullable(),
});
export const jobTypePayload = z.object({
  name: z.string(),
  requiredSkill: z.string().optional(),
  defaultChecklist: z.array(z.object({ label: z.string(), priceCode: z.string().optional() })),
});

const base = {
  id: z.string(),
  status: z.enum(['PENDING', 'ACCEPTED', 'REJECTED']),
  confidence: z.number(),
  evidence: z.number(),
};
export const suggestionSchema = z.discriminatedUnion('kind', [
  z.object({ ...base, kind: z.literal('CUSTOMER'), payload: customerPayload }),
  z.object({ ...base, kind: z.literal('PRICE_ITEM'), payload: pricePayload }),
  z.object({ ...base, kind: z.literal('JOB_TYPE'), payload: jobTypePayload }),
  z.object({ ...base, kind: z.literal('SUPPLIER'), payload: supplierPayload }),
  z.object({ ...base, kind: z.literal('MATERIAL'), payload: materialPayload }),
]);
export type Suggestion = z.infer<typeof suggestionSchema>;

const pickerSchema = z.union([
  z.object({ enabled: z.literal(false) }),
  z.object({
    enabled: z.literal(true),
    apiKey: z.string(),
    appId: z.string(),
    clientId: z.string(),
  }),
]);
export type PickerConfig = z.infer<typeof pickerSchema>;

export interface SignupInput {
  businessName: string;
  /** התחום — ממנו השרת גוזר קטגוריה, סוגי עבודות ותהליכים. */
  trade: TradeKey;
  /** מה יוצג לעסק. בלי — ברירת המחדל של התחום. */
  features?: string[];
  ownerName: string;
  email: string;
  password: string;
  /** שפת הממשק שנבחרה בשלב הראשון — נשמרת לעסק. */
  locale?: 'he' | 'en';
}

export type AcceptResult = { accepted: number; skipped: number; failed: Array<{ id: string; reason: string }> };

export const setupApi = {
  signup: (body: SignupInput) =>
    // אין עדיין עסק — הבקשה לא נושאת X-Tenant.
    request('/onboarding/signup', {
      method: 'POST',
      body,
      tenant: null,
      schema: authResponseSchema,
    }),

  /** הרשמה עם Google: כתובת ההסכמה; החזרה ל-/onboarding?google_ticket=... */
  googleSignupStart: () =>
    request('/auth/google/start?intent=signup', {
      tenant: null,
      schema: z.object({ authUrl: z.string().url() }),
    }),

  /** הכרטיס מ-Google + מה שנאסף בשיחה → עסק חדש וסשן. */
  googleSignup: (body: { ticket: string; businessName: string; ownerName: string; locale?: 'he' | 'en' }) =>
    request('/onboarding/signup/google', {
      method: 'POST',
      body,
      tenant: null,
      schema: authResponseSchema,
    }),

  status: (signal?: AbortSignal) =>
    request('/onboarding-import/status', { schema: setupStatusSchema, signal }),

  connectGoogle: () =>
    request('/integrations/google/connect?returnTo=onboarding', {
      schema: z.object({ authUrl: z.string().url() }),
    }),

  picker: () => request('/onboarding-import/picker', { schema: pickerSchema }),

  start: (driveFileIds: string[]) =>
    request('/onboarding-import/runs', {
      method: 'POST',
      body: { driveFileIds },
      schema: z.object({ runId: z.string() }),
    }),

  suggestions: (signal?: AbortSignal) =>
    request('/onboarding-import/suggestions', { schema: z.array(suggestionSchema), signal }),

  update: (
    id: string,
    patch: { payload?: Record<string, unknown>; status?: 'PENDING' | 'REJECTED' },
  ) => request(`/onboarding-import/suggestions/${id}`, { method: 'PATCH', body: patch }),

  accept: () =>
    request('/onboarding-import/accept', {
      method: 'POST',
      body: {},
      schema: z.object({
        accepted: z.number(),
        skipped: z.number(),
        failed: z.array(z.object({ id: z.string(), reason: z.string() })),
      }),
    }),

  finish: () =>
    request('/onboarding-import/finish', {
      method: 'POST',
      schema: z.object({ stage: setupStageSchema }),
    }),
};
